// pages/devolucionesVenta/DevolucionVentaList.tsx
// Diseño Notion/Linear — espejo de DevolucionCompraList
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { toast } from '../../utils/toast';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Stack,
  Tab,
  Tabs,
  Skeleton,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AssignmentReturn as ReturnIcon,
  CheckCircleOutline as ProcessedIcon,
  Today as TodayIcon,
  Inventory2 as ArticleIcon,
} from '@mui/icons-material';
import { DarkModeContext } from '../../context/darkModeContext';
import devolucionVentaApi, { IDevolucionVenta } from '../../services/api/devolucionVentaApi';
import { DetalleDevolucionVentaModal } from './DetalleDevolucionVentaModal';

// ── Paleta Notion/Linear ──
const P = {
  light: {
    bg: '#FFFFFF', bgS: '#F7F7F5', bgT: '#F1F1EF', text: '#37352F',
    textS: '#6B6B6B', textT: '#9B9A97', border: '#E9E9E7', borderH: '#D3D3D0',
    accent: '#2383E2', accentBg: '#E8F0FE',
    success: '#0F7B6C', successBg: '#DBEDDB',
    shadow: '0 1px 3px rgba(0,0,0,0.04)', hover: 'rgba(55,53,47,0.04)',
  },
  dark: {
    bg: '#191919', bgS: '#202020', bgT: '#2B2B2B', text: '#E8E8E6',
    textS: '#9B9A97', textT: '#6B6B6B', border: '#333333', borderH: '#444444',
    accent: '#529CCA', accentBg: '#143A52',
    success: '#4DAB9A', successBg: '#1A2F2A',
    shadow: '0 1px 3px rgba(0,0,0,0.2)', hover: 'rgba(255,255,255,0.04)',
  },
};

// ── KPI Card ──
const KPICard: React.FC<{
  label: string; value: string | number; icon: React.ReactElement;
  accent?: string; accentBg?: string; dark: boolean;
}> = ({ label, value, icon, accent, accentBg, dark }) => {
  const p = dark ? P.dark : P.light;
  return (
    <Box sx={{
      p: 2.5, borderRadius: '10px', border: `1px solid ${p.border}`, bgcolor: p.bg,
      boxShadow: p.shadow, flex: 1, minWidth: 160,
      transition: 'all 0.15s', '&:hover': { borderColor: p.borderH },
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '8px',
          bgcolor: accentBg || p.bgT,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: accent || p.textS, '& svg': { fontSize: 18 },
        }}>
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: p.textT, letterSpacing: '0.02em' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: p.text, fontFeatureSettings: "'tnum'" }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

// ── Action Button ──
const ActionBtn: React.FC<{
  tip: string; icon: React.ReactElement; onClick?: () => void;
  disabled?: boolean; dark: boolean; variant?: 'default' | 'accent';
}> = ({ tip, icon, onClick, disabled, dark, variant = 'default' }) => {
  const p = dark ? P.dark : P.light;
  const colors = {
    default: { bg: p.bgT, hover: p.borderH, text: p.textS },
    accent: { bg: p.accentBg, hover: dark ? '#1C4466' : '#D4E4F7', text: p.accent },
  };
  const col = colors[variant];
  return (
    <Tooltip title={tip} arrow>
      <span>
        <IconButton onClick={onClick} disabled={disabled} size="small" sx={{
          width: 30, height: 30, borderRadius: '7px',
          color: disabled ? p.textT : col.text,
          bgcolor: disabled ? 'transparent' : col.bg,
          opacity: disabled ? 0.4 : 1,
          '& svg': { fontSize: 16 },
          '&:hover': { bgcolor: disabled ? 'transparent' : col.hover },
        }}>
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

// ── MAIN COMPONENT ──
interface Props {
  onNuevaDevolucion?: () => void;
}

export const DevolucionVentaList: React.FC<Props> = ({ onNuevaDevolucion }) => {
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const [devoluciones, setDevoluciones] = useState<IDevolucionVenta[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [tabFilter, setTabFilter] = useState('todas');
  const [selectedDevolucion, setSelectedDevolucion] = useState<IDevolucionVenta | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => { loadDevoluciones(); }, []);

  const loadDevoluciones = async () => {
    try {
      setLoading(true);
      const data = await devolucionVentaApi.listar();
      setDevoluciones(data ?? []);
    } catch {
      toast.error('No se pudieron cargar las devoluciones');
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalle = async (id: number) => {
    try {
      const detalle = await devolucionVentaApi.buscarUna(id);
      setSelectedDevolucion(detalle);
      setModalOpen(true);
    } catch {
      toast.error('No se pudo cargar el detalle');
    }
  };

  // ── KPIs ──
  const hoy = new Date().toDateString();
  const kpis = useMemo(() => {
    const totalUnidades = devoluciones.reduce(
      (sum, d) => sum + (d.detalles?.reduce((s, det) => s + det.Cantidad, 0) ?? 0), 0,
    );
    const hoyCount = devoluciones.filter(
      (d) => d.detalles?.[0]?.FechaDevolucion
        && new Date(d.detalles[0].FechaDevolucion).toDateString() === hoy,
    ).length;
    return { total: devoluciones.length, totalUnidades, hoyCount };
  }, [devoluciones]);

  // ── Filtered list ──
  const filtered = useMemo(() => {
    let list = devoluciones;
    if (tabFilter === 'hoy') {
      list = list.filter(
        (d) => d.detalles?.[0]?.FechaDevolucion
          && new Date(d.detalles[0].FechaDevolucion).toDateString() === hoy,
      );
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (d) =>
          d.CodigoFacturaVenta.toLowerCase().includes(q) ||
          d.Id.toString().includes(q) ||
          d.detalles?.some((det) => det.articulo?.Nombre?.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [devoluciones, tabFilter, searchTerm]);

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const cellSx = {
    borderBottom: `1px solid ${p.border}`,
    color: p.text, fontSize: '0.875rem', py: 1.5,
  };
  const headCellSx = {
    ...cellSx,
    color: p.textT, fontWeight: 600, fontSize: '0.72rem',
    letterSpacing: '0.04em', textTransform: 'uppercase' as const,
    bgcolor: p.bgS, borderTop: `1px solid ${p.border}`,
  };

  const getFechaDevolucion = (dev: IDevolucionVenta) => {
    const fecha = dev.detalles?.[0]?.FechaDevolucion;
    if (!fecha) return '—';
    return new Date(fecha).toLocaleDateString('es-CO', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  const getTotalUnidades = (dev: IDevolucionVenta) =>
    dev.detalles?.reduce((s, d) => s + d.Cantidad, 0) ?? 0;

  return (
    <>
    <Box>
        {/* ── KPIs ── */}
        <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <KPICard
            label="TOTAL DEVOLUCIONES"
            value={loading ? '—' : kpis.total}
            icon={<ReturnIcon />}
            dark={darkMode}
          />
          <KPICard
            label="UNIDADES DEVUELTAS"
            value={loading ? '—' : kpis.totalUnidades}
            icon={<ArticleIcon />}
            accent={p.accent}
            accentBg={p.accentBg}
            dark={darkMode}
          />
          <KPICard
            label="HOY"
            value={loading ? '—' : kpis.hoyCount}
            icon={<TodayIcon />}
            accent={p.success}
            accentBg={darkMode ? P.dark.successBg : P.light.successBg}
            dark={darkMode}
          />
        </Stack>

        {/* ── Header ── */}
        <Box sx={{
          bgcolor: p.bg, border: `1px solid ${p.border}`,
          borderRadius: '10px 10px 0 0', px: 2.5, pt: 2, pb: 0, boxShadow: p.shadow,
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: p.text }}>
                Historial de Devoluciones
              </Typography>
              {!loading && (
                <Box sx={{
                  px: 1, py: 0.25, borderRadius: '5px',
                  bgcolor: p.bgT, color: p.textS, fontSize: '0.78rem', fontWeight: 600,
                }}>
                  {filtered.length}
                </Box>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Actualizar" arrow>
                <IconButton
                  onClick={loadDevoluciones}
                  size="small"
                  sx={{ color: p.textS, bgcolor: p.bgT, borderRadius: '7px', width: 32, height: 32, '& svg': { fontSize: 17 } }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* ── Tabs + Búsqueda ── */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Tabs
              value={tabFilter}
              onChange={(_, v) => { setTabFilter(v); setPage(0); }}
              sx={{
                minHeight: 0,
                '& .MuiTab-root': {
                  minHeight: 36, py: 0.5, px: 1.5, textTransform: 'none',
                  fontWeight: 500, fontSize: '0.85rem', color: p.textS, minWidth: 0,
                },
                '& .Mui-selected': { color: p.text, fontWeight: 600 },
                '& .MuiTabs-indicator': { bgcolor: p.text, height: 2 },
              }}
            >
              <Tab label="Todas" value="todas" />
              <Tab
                label={`Hoy${kpis.hoyCount > 0 ? ` (${kpis.hoyCount})` : ''}`}
                value="hoy"
              />
            </Tabs>
            <TextField
              size="small"
              placeholder="Buscar factura, artículo o ID..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: p.textT }} />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: p.bgS, border: `1px solid ${p.border}`,
                  borderRadius: '7px', fontSize: '0.85rem', color: p.text,
                  '& fieldset': { border: 'none' },
                },
              }}
              sx={{ '& .MuiInputBase-input': { py: 0.75 }, width: 260 }}
            />
          </Stack>
        </Box>

        {/* ── Tabla ── */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: p.bg, border: `1px solid ${p.border}`,
            borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>ID</TableCell>
                  <TableCell sx={headCellSx}>Factura</TableCell>
                  <TableCell sx={headCellSx} align="center">Artículos</TableCell>
                  <TableCell sx={headCellSx} align="center">Unidades</TableCell>
                  <TableCell sx={headCellSx}>Fecha</TableCell>
                  <TableCell sx={headCellSx} align="center">Estado</TableCell>
                  <TableCell sx={headCellSx} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {[1, 2, 3, 4, 5, 6, 7].map((j) => (
                        <TableCell key={j} sx={cellSx}>
                          <Skeleton variant="text" width={j === 7 ? 50 : '75%'} height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ ...cellSx, py: 7 }}>
                      <Stack alignItems="center" spacing={1.5}>
                        <ReturnIcon sx={{ fontSize: 40, color: p.textT }} />
                        <Typography sx={{ color: p.textT, fontSize: '0.9rem' }}>
                          {searchTerm
                            ? 'Sin resultados para la búsqueda'
                            : tabFilter === 'hoy'
                            ? 'No hay devoluciones hoy'
                            : 'No hay devoluciones registradas'}
                        </Typography>
                        {!searchTerm && onNuevaDevolucion && (
                          <Box
                            onClick={onNuevaDevolucion}
                            sx={{
                              mt: 0.5, px: 2, py: 0.75, borderRadius: '7px',
                              border: `1px solid ${p.border}`, color: p.textS,
                              fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer',
                              '&:hover': { bgcolor: p.bgT },
                            }}
                          >
                            Registrar primera devolución
                          </Box>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((dev) => {
                    const unidades = getTotalUnidades(dev);
                    return (
                      <TableRow
                        key={dev.Id}
                        sx={{ '&:hover': { bgcolor: p.hover }, transition: 'background 0.1s' }}
                      >
                        {/* ID */}
                        <TableCell sx={cellSx}>
                          <Typography sx={{ fontSize: '0.8rem', color: p.textT, fontWeight: 500 }}>
                            #{dev.Id}
                          </Typography>
                        </TableCell>

                        {/* Factura */}
                        <TableCell sx={cellSx}>
                          <Typography sx={{ fontWeight: 700, color: p.text, fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {dev.CodigoFacturaVenta}
                          </Typography>
                        </TableCell>

                        {/* Artículos (tipos distintos) */}
                        <TableCell sx={cellSx} align="center">
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, borderRadius: '50%',
                            bgcolor: p.bgT, color: p.textS,
                            fontSize: '0.82rem', fontWeight: 600,
                          }}>
                            {dev.detalles?.length ?? 0}
                          </Box>
                        </TableCell>

                        {/* Unidades totales */}
                        <TableCell sx={cellSx} align="center">
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            px: 1.25, py: 0.3, borderRadius: '5px',
                            bgcolor: p.accentBg, color: p.accent,
                            fontSize: '0.78rem', fontWeight: 700,
                          }}>
                            {unidades} ud{unidades !== 1 ? 's' : ''}
                          </Box>
                        </TableCell>

                        {/* Fecha */}
                        <TableCell sx={cellSx}>
                          <Typography sx={{ color: p.textS, fontSize: '0.85rem' }}>
                            {getFechaDevolucion(dev)}
                          </Typography>
                        </TableCell>

                        {/* Estado — siempre Procesada */}
                        <TableCell sx={cellSx} align="center">
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.75,
                            px: 1.25, py: 0.4, borderRadius: '5px',
                            bgcolor: darkMode ? P.dark.successBg : P.light.successBg,
                            color: darkMode ? P.dark.success : P.light.success,
                            fontSize: '0.75rem', fontWeight: 600,
                          }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', flexShrink: 0 }} />
                            Procesada
                          </Box>
                        </TableCell>

                        {/* Acciones */}
                        <TableCell sx={cellSx} align="right">
                          <ActionBtn
                            tip="Ver detalle"
                            icon={<VisibilityIcon />}
                            onClick={() => handleVerDetalle(dev.Id)}
                            dark={darkMode}
                            variant="accent"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && filtered.length > rowsPerPage && (
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Filas:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{
                borderTop: `1px solid ${p.border}`,
                color: p.textS,
                '& .MuiTablePagination-select': { color: p.text },
              }}
            />
          )}
        </Paper>
      </Box>

      <DetalleDevolucionVentaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        devolucion={selectedDevolucion}
        dark={darkMode}
      />
    </>
  );
};

export default DevolucionVentaList;
